﻿'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

const filterChip = cva(
  [
    'inline-flex shrink-0 items-center gap-1.5 rounded-full whitespace-nowrap',
    'h-9 px-3.5 text-xs font-medium transition-colors',
    'touch-manipulation focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
  ],
  {
    variants: {
      active: {
        true: 'bg-brand-primary text-on-brand',
        false: 'border border-border bg-surface text-text-primary hover:bg-surface-sunken',
      },
    },
    defaultVariants: { active: false },
  },
);

interface FilterChipProps extends VariantProps<typeof filterChip> {
  label: string;
  onClick: () => void;
  onClear?: () => void;
  clearable?: boolean;
  className?: string;
}

export function FilterChip({ label, active, onClick, onClear, clearable, className }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(filterChip({ active }), className)}
      aria-pressed={active ?? false}
    >
      {!active && <PlusIcon className="size-3.5 text-text-tertiary" weight="bold" />}
      <span>{label}</span>
      {active && clearable && onClear && (
        <span
          role="button"
          tabIndex={-1}
          aria-label="Limpar filtro"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onClear();
            }
          }}
          className="-mr-1 ml-0.5 inline-flex size-5 items-center justify-center rounded-full hover:bg-white/20"
        >
          <XIcon className="size-3" weight="bold" />
        </span>
      )}
    </button>
  );
}
